import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import PageHeader from "@/components/feature/PageHeader";
import { relationships } from "@/mocks/relationships";
import { characters } from "@/mocks/characters";

const TYPE_KEYS = ["全部", "师兄关系", "师姐互动", "同门情谊", "师徒情", "暗线纠葛"];

const CLOSENESS_TONE: Record<string, string> = {
  亲密: "bg-primary-500",
  融洽: "bg-secondary-500",
  微妙: "bg-accent-500",
  疏离: "bg-background-300",
};

const CLOSENESS_TEXT: Record<string, string> = {
  亲密: "text-primary-700 bg-primary-100",
  融洽: "text-secondary-700 bg-secondary-100",
  微妙: "text-accent-700 bg-accent-100",
  疏离: "text-foreground-600 bg-background-200",
};

const ROLE_TONE: Record<string, string> = {
  师兄: "bg-primary-100 text-primary-700",
  师姐: "bg-secondary-100 text-secondary-700",
  师弟: "bg-accent-100 text-accent-700",
  师妹: "bg-secondary-100 text-secondary-700",
  师尊: "bg-accent-100 text-accent-700",
  对手: "bg-primary-100 text-primary-700",
  外门: "bg-background-200 text-foreground-700",
};

export default function Relations() {
  const [active, setActive] = useState("全部");
  const list = useMemo(
    () => (active === "全部" ? relationships : relationships.filter((r) => r.type === active)),
    [active],
  );

  return (
    <>
      <Navbar />
      <main>
        <PageHeader
          eyebrow="RELATIONS MAP"
          title="人物关系图"
          subtitle="师兄关系亲疏、师姐互动名场面、同门情谊与师徒情、暗线纠葛，云隐峰的人物脉络一页厘清。"
          crumbs={[{ label: "首页", path: "/" }, { label: "人物关系" }]}
          image="https://readdy.ai/api/search-image?query=Ancient%20Chinese%20cultivation%20sect%20map%20with%20portraits%20connected%20by%20thin%20gold%20lines%20across%20a%20misty%20valley%2C%20ink%20wash%20style%2C%20warm%20vermilion%20and%20gold%2C%20network%20of%20relationships%20visualized%2C%20cinematic%2C%20refined%20detail&width=1920&height=760&seq=page-relations-01&orientation=landscape"
        />

        <section className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="人物关系类型筛选">
            {TYPE_KEYS.map((t) => {
              const activeTab = active === t;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={activeTab}
                  onClick={() => setActive(t)}
                  className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-colors ${
                    activeTab
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            {list.map((r) => (
              <article key={r.id} className="flex flex-col rounded-lg border border-background-200 bg-background-100 p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="rounded-md bg-background-50 px-3 py-1 text-xs text-foreground-600 ring-1 ring-background-200">
                    {r.type}
                  </span>
                  <span className={`rounded-full px-3 py-1 text-xs ${CLOSENESS_TEXT[r.closeness]}`}>
                    关系{r.closeness}
                  </span>
                </div>
                <h2 className="mt-4 font-heading text-xl text-foreground-950">{r.pair}</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{r.summary}</p>
                <div className="mt-auto flex items-start gap-2 rounded-lg bg-background-50 p-4 pt-4 ring-1 ring-background-200">
                  <i className="ri-quill-pen-line mt-0.5 text-secondary-600" />
                  <p className="text-sm leading-6 text-foreground-600">
                    <span className="font-medium text-foreground-800">暗线 / 细节：</span>
                    {r.note}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
            <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-medium tracking-[0.3em] text-primary-600">CAST</p>
                <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
                  云隐峰人物名册
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">
                  每一位角色都牵动着数条关系线，是理解师兄关系与同门情谊的关键。
                </p>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {characters.map((c) => (
                <article key={c.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all hover:-translate-y-1">
                  <div className="relative h-52 w-full overflow-hidden md:h-56">
                    <img src={c.portrait} alt={`${c.name} ${c.title} 水墨人像`} className="h-full w-full object-cover object-top" />
                    <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs ${ROLE_TONE[c.role]}`}>
                      {c.role}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-lg text-foreground-950">{c.name}</h3>
                      <span className="text-xs text-foreground-500">{c.title}</span>
                    </div>
                    <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-2">{c.desc}</p>
                    <div className="mt-3 flex items-center gap-2 text-xs text-foreground-500">
                      <span>关系热度</span>
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                        <div className="h-full rounded-full bg-primary-500" style={{ width: `${c.closeness}%` }}></div>
                      </div>
                      <span className="text-primary-600">{c.closeness}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/cp-lines"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                继续查看 CP 线脉络 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "人物关系图",
              description: "师兄关系亲疏、师姐互动名场面、同门情谊与师徒情、暗线纠葛的全梳理。",
              inLanguage: "zh-CN",
              about: "师兄啊师兄人物关系",
            }),
          }}
        />
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}